import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Markdown } from '@Sage/hoi-poi-ui';

import Map from 'components/Map';
import MapCircle from 'components/Map/MapCircle';
import MapMarker from 'components/Map/MapMarker';
import MapMarkerWithTooltip from 'components/Map/MapMarkerWithTooltip';
import { COMPANY_ICON, COMPANY_ICON_GREY } from 'constants/Images';
import { getLiteral } from 'utils/getLiteral';

import { LEADS_LITERALS } from './literals';

const RADIUS_ANIM_MS = 380;

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

/**
 * Google Maps Circle options only accept real hex/rgb — not `var(--token)` from useTheme().
 */
const LEADS_MAP_RADIUS_CIRCLE = {
    fillColor: '#007E45',
    fillOpacity: 0.18,
    strokeColor: '#007E45',
    strokeOpacity: 0.8,
    strokeWeight: 1,
};

/** Tooltip: name, then business type (category) on the next line — no separate field label. */
const buildLeadTooltipContent = (lead) => (
    <div className="fm-leads-modal__map-tooltip">
        <div className="fm-leads-modal__map-tooltip-name">{lead.name}</div>
        {lead.category ? (
            <div className="fm-leads-modal__map-tooltip-type">{lead.category}</div>
        ) : null}
    </div>
);

const isLeadSelected = (selectedIds, leadId) =>
    selectedIds.some((sid) => String(sid) === String(leadId));

/** Markers match the list: same bucket as `LeadList` (`activeLeads`). */
const LeadsMap = ({
    activeLeads = [],
    selectedIds = [],
    origin,
    radiusKm,
    onOriginChange,
    onToggleLead,
}) => {
    const targetRadiusMeters = Number(radiusKm) * 1000;
    const [animatedRadiusMeters, setAnimatedRadiusMeters] = useState(targetRadiusMeters);
    const animatedRadiusRef = useRef(targetRadiusMeters);
    const rafRef = useRef(null);

    useEffect(() => {
        const start = animatedRadiusRef.current;
        if (Math.abs(start - targetRadiusMeters) < 0.5) {
            animatedRadiusRef.current = targetRadiusMeters;
            setAnimatedRadiusMeters(targetRadiusMeters);
            return undefined;
        }

        if (rafRef.current != null) cancelAnimationFrame(rafRef.current);

        const t0 = performance.now();

        const tick = (now) => {
            const elapsed = now - t0;
            const t = Math.min(1, elapsed / RADIUS_ANIM_MS);
            const next = start + (targetRadiusMeters - start) * easeOutCubic(t);
            animatedRadiusRef.current = next;
            setAnimatedRadiusMeters(next);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                animatedRadiusRef.current = targetRadiusMeters;
                setAnimatedRadiusMeters(targetRadiusMeters);
                rafRef.current = null;
            }
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [targetRadiusMeters]);

    const circleOptions = useMemo(
        () => ({
            center: origin,
            radius: animatedRadiusMeters,
            options: LEADS_MAP_RADIUS_CIRCLE,
        }),
        [animatedRadiusMeters, origin],
    );

    return (
        <div className="fm-leads-modal__map">
            <div className="fm-leads-modal__map-help">
                <Markdown content={getLiteral(LEADS_LITERALS.dragPinHelp)} />
            </div>
            <Map center={origin} zoom={13}>
                <MapCircle {...circleOptions} />
                <MapMarker
                    position={origin}
                    draggable
                    title={getLiteral(LEADS_LITERALS.searchOrigin)}
                    onDragEnd={(event) => {
                        const lat = event?.latLng?.lat?.();
                        const lng = event?.latLng?.lng?.();
                        if (Number.isFinite(lat) && Number.isFinite(lng))
                            onOriginChange({ lat, lng });
                    }}
                />
                {activeLeads.map((lead) => (
                    <MapMarkerWithTooltip
                        key={lead.id}
                        position={lead.position}
                        icon={{
                            url: isLeadSelected(selectedIds, lead.id)
                                ? COMPANY_ICON
                                : COMPANY_ICON_GREY,
                        }}
                        tooltipContent={buildLeadTooltipContent(lead)}
                        onClick={() => onToggleLead(lead.id)}
                    />
                ))}
            </Map>
        </div>
    );
};

export default memo(LeadsMap);
